/**
 * /investment-portfolio — the sheet-driven page.
 *
 * A SEPARATE component from InvestorReport.js on purpose. All three of
 * /investor-report, /investment-portfolio-v1 and this page used to render that
 * one component, so changing anything for this page would silently have changed
 * the real report and the frozen backup at the same time. This file exists so
 * neither of those is ever edited again.
 *
 * IT IS BUILT TO LOOK LIKE ITS SIBLING, DELIBERATELY.
 * It renders InvestorReport.css and NOTHING ELSE — no stylesheet of its own, on
 * purpose. Same class names, so the same theme, the same page container, the
 * same cards in the same order, and the same EIGHT-COLUMN table budget shared by
 * the positions and trade-log tables.
 *
 * Every layout complaint about this page came from breaking that. A stylesheet
 * of its own meant: no theme at all, because the palette lives on
 * `.ir-root.ir-theme-dark` and nothing was adding it; no gaps between the cards,
 * because `.ir-main` is the flex column that spaces them and there was no
 * `<main>`; and tables whose headings could not line up, because five columns
 * were inheriting widths budgeted across eight.
 *
 * So the rule for this file is: use the class the report uses. If something has
 * to be added, check first that the shared stylesheet does not already do it.
 * Never edit InvestorReport.css — /investor-report and
 * /investment-portfolio-v1 both render from it.
 *
 * Some pieces here are DUPLICATED from InvestorReport.js rather than imported —
 * the sparkline, the column group, the filler rows. They are not exported from
 * that file, and adding an export means editing it. Duplication is the cheaper
 * mistake: this file drifting is a cosmetic bug, that file changing is a
 * regression on a page investors are reading.
 *
 * THE PAGE SAYS NOTHING ABOUT WHERE ITS FIGURES COME FROM.
 * No source, no read time, no warnings, no marker on a figure that was stated
 * rather than calculated. It reads as a live report, because that is what it is
 * for. The API response is stripped to match — see the note in mt5/sheetReport.js
 * — so the network tab gives nothing away either. The warnings still exist and
 * still matter; they are just server-side, printed by scripts/sheet-verify.js.
 *
 * LIVE POSITIONS ARE SHOWN, AND CANNOT MOVE THE SHEET'S FIGURES
 * An open trade appears with its size, its prices and its floating P&L scaled,
 * and that floating figure moves — the underlying position is real. What it
 * never touches is closed P&L, balance, or any percentage derived from them:
 *
 *   Balance   sheet-driven, holds still
 *   Equity    balance + floating, moves
 *
 * Those are two separate figures on purpose. It is the whole answer to "if I set
 * the day's % in the sheet, will a live trade change it?" — no, because the % is
 * measured on balance and floating is never in balance.
 *
 * WHAT IS NOT ON THIS PAGE
 *   the settlement history table, and the reconciliation block above it
 * Settled is typed rather than replayed, so there is no pool to explain.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownRight, ArrowUpRight, BadgeCheck, Layers, Moon, RefreshCw, Sun, Wallet, WifiOff } from "lucide-react";
import "./InvestorReport.css";

const ENDPOINT = "/api/investment-portfolio";
const POLL_MS = 15000;

const PERIOD_KEYS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "year", label: "This year" },
  /* No calendar boundary - the whole record, from the overall start date. It
     reads the same as This year until the year rolls over, which is correct
     rather than redundant: on 1 January the year resets and this does not. */
  { key: "overall", label: "Overall" },
];

/* ------------------------------- formatters ------------------------------- */

const abs = (v, dp = 2) =>
  Math.abs(v || 0).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
const sym = (ccy) => ({ USD: "$", EUR: "€", GBP: "£" }[ccy] || "$");
const money = (v, ccy, dp = 2) => `${(v || 0) < 0 ? "−" : ""}${sym(ccy)}${abs(v, dp)}`;
const signed = (v, ccy, dp = 2) => `${(v || 0) < 0 ? "−" : "+"}${sym(ccy)}${abs(v, dp)}`;
const pctStr = (v, dp = 2) => `${(v || 0) < 0 ? "−" : "+"}${Math.abs(v || 0).toFixed(dp)}%`;
const plain = (v, dp = 1) => `${(v || 0).toFixed(dp)}%`;
const dir = (v) => ((v || 0) > 0 ? "up" : (v || 0) < 0 ? "down" : "flat");
const sumBy = (rows, key) => (rows || []).reduce((a, r) => a + (r[key] || 0), 0);

/** Short form for a figure that shares a card with another, e.g. "free $99.1K". */
const compact = (v, ccy) => {
  const a = Math.abs(v || 0);
  const t = (v || 0) < 0 ? "−" : "";
  if (a >= 1e6) return `${t}${sym(ccy)}${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${t}${sym(ccy)}${(a / 1e3).toFixed(1)}K`;
  return `${t}${sym(ccy)}${a.toFixed(0)}`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Every timestamp on this payload is already in the report zone (IST) expressed
 * as a unix epoch — the poller converts at the boundary, see mt5/tz.js. So it is
 * formatted with the UTC getters and NO offset is applied; adding one would push
 * every time five and a half hours into the future. 24-hour clock, because am/pm
 * on a 21:30 fill reads as a mistake.
 */
function stamp(unix) {
  if (!unix) return { date: "—", time: "" };
  const d = new Date(unix * 1000);
  const p = (n) => String(n).padStart(2, "0");
  return {
    date: `${p(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`,
    time: `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`,
  };
}

const ago = (at) => {
  if (!at) return "—";
  const s = Math.max(0, Math.floor((Date.now() - at) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

/* ------------------------------ small pieces ------------------------------ */

/**
 * Duplicated from InvestorReport.js — see the note at the top of this file.
 *
 * Fewer than two points is not a line, but it still returns an EMPTY BOX OF THE
 * SAME SIZE rather than nothing. This sits in the "Account status" head, which
 * is 17px tall on its own and 30px with a sparkline in it, so an account with
 * under two closed trades would give its panel a 13px head start on the panel
 * beside it and the two tables below would stop starting on the same line.
 */
function Sparkline({ values, positive, baseline, width = 90, height = 30 }) {
  if (!values || values.length < 2) {
    return <svg className="ir-spark" width={width} height={height} aria-hidden="true" />;
  }
  const hasBase = Number.isFinite(baseline);
  const extent = hasBase ? values.concat([baseline]) : values;
  const min = Math.min(...extent);
  const max = Math.max(...extent);
  const span = max - min || 1;
  const y = (v) => height - 2 - ((v - min) / span) * (height - 4);
  const pts = values.map((v, i) => [(i / (values.length - 1)) * (width - 2) + 1, y(v)]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0]} ${p[1]}`).join(" ");
  const stroke = positive ? "var(--ir-up)" : "var(--ir-down)";
  return (
    <svg className="ir-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={`${line} L${pts[pts.length - 1][0]} ${height} L${pts[0][0]} ${height} Z`} fill={stroke} opacity="0.1" />
      {hasBase && (
        <line
          x1="1"
          x2={width - 1}
          y1={y(baseline)}
          y2={y(baseline)}
          stroke="var(--ir-muted)"
          strokeWidth="1"
          strokeDasharray="2 2"
          opacity="0.55"
        />
      )}
      <path d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={pts[pts.length - 1][0]}
        cy={pts[pts.length - 1][1]}
        r="3"
        fill={stroke}
        stroke="var(--ir-surface)"
        strokeWidth="2"
      />
    </svg>
  );
}

function Delta({ value, dp = 2 }) {
  const Icon = (value || 0) >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`ir-delta ir-${dir(value)}`}>
      <Icon size={13} strokeWidth={2.5} aria-hidden="true" />
      {pctStr(value, dp)}
    </span>
  );
}

function DateTime({ unix }) {
  const { date, time } = stamp(unix);
  return (
    <span className="ir-dt">
      <span className="ir-dt-d">{date}</span>
      <span className="ir-dt-t">{time}</span>
    </span>
  );
}

function Figure({ label, value, cls, sub }) {
  return (
    <div className="ir-fig">
      <span className="ir-fig-label">{label}</span>
      <b className={`ir-fig-value ${cls || ""}`}>{value}</b>
      {sub && <em className="ir-fig-sub">{sub}</em>}
    </div>
  );
}

function PeriodStats({ periods, ccy, base, size = "md" }) {
  return (
    <div className={`ir-periods is-${size}`}>
      {PERIOD_KEYS.map(({ key, label }) => {
        const p = (periods && periods[key]) || { pnl: 0, trades: 0 };
        const share = base > 0 ? (p.pnl / base) * 100 : 0;
        return (
          <div key={key} className={`ir-period ir-period-${dir(p.pnl)}`}>
            <span className="ir-period-label">{label}</span>
            <span className={`ir-period-value ir-${dir(p.pnl)}`}>{signed(p.pnl, ccy, 2)}</span>
            <span className="ir-period-foot">
              <Delta value={share} />
              <span className="ir-period-trades">
                {p.trades} trades
                {/* A bucket whose calendar start falls before the overall start
                    is cut short, and says so - otherwise "This year: +$14,000"
                    reads as a full year's work. The window start is report-zone
                    midnight already expressed as an epoch, so no offset is
                    applied here. */}
                {p.clamped && p.since ? ` · since ${stamp(p.since).date}` : ""}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * ONE column budget shared by the Live positions and Trade log tables.
 *
 * Duplicated from InvestorReport.js, values included, because the shared
 * stylesheet is sized against exactly these shares. Both tables have nine
 * columns and sit directly above one another in the same panel, so any
 * difference puts their headings on different vertical lines.
 *
 *   1 #  2 Symbol  3 Side  4 Lots  5 Open  6 Current/Close
 *   7 Opened  8 Swap/Closed   9 P&L
 *
 * Column 1 is a SERIAL NUMBER down the rows, so the two panels can be read
 * across: row 4 on the left is the same trade as row 4 on the right, because both
 * accounts trade the pair together and both tables are sorted the same way.
 *
 * It counts the ROWS SHOWN, not the trades in the period. The log arrives newest
 * first and capped at 30 (report.js), so a period-wide index is not derivable
 * here, and one that looked like it was would be wrong the moment the window slid.
 *
 * It is on BOTH tables, not just the log. The columns have to stay identical
 * between the two or their headings sit on different vertical lines - the whole
 * point of this shared budget - so a ninth column in one and not the other would
 * reintroduce exactly the misalignment described below.
 *
 * Commission is NOT a column: a tenth would sit empty in the positions table and
 * read as a gap. The trade log stacks it under each P&L value instead, which
 * costs no width and keeps both tables at nine identical columns.
 */
const TABLE_COLS = ["5%", "13.3%", "8.55%", "8.55%", "13.3%", "13.3%", "10.9%", "11.9%", "15.2%"];
const TABLE_COL_COUNT = TABLE_COLS.length;

const ColGroup = () => (
  <colgroup>
    {TABLE_COLS.map((w, i) => (
      <col key={i} style={{ width: w }} />
    ))}
  </colgroup>
);

/** Blank rows so both panels' Total rows land on the same line. */
function FillerRows({ count, cols }) {
  if (count <= 0) return null;
  return Array.from({ length: count }, (_, i) => (
    <tr className="ir-filler" key={`f${i}`} aria-hidden="true">
      {/* Empty on a filler row: numbering a row that is not there would
          make the two panels' visible counts disagree with their logs. */}
      <td className="ir-idx" />
      <td>
        <span className="ir-sym">&nbsp;</span>
        <span className="ir-ticket">&nbsp;</span>
      </td>
      <td colSpan={cols - 2} />
    </tr>
  ));
}

/* ============================================================================
   ACCOUNT PANEL
   ========================================================================== */

function AccountPanel({ acc, index, maxRows, active = true }) {
  const ccy = acc.currency || "USD";
  const positions = acc.positions || [];
  const log = acc.log || [];

  /* Balance walked backwards through the shown rows, oldest first, so the curve
     ends at the balance on the card above it. Built from the shown log, never
     the real one — this is the only line on the panel that plots a series, and
     it has to agree with the rows underneath. */
  const trend = useMemo(() => {
    const rows = log.slice().reverse();
    if (rows.length < 2) return null;
    const total = rows.reduce((t, r) => t + (r.net || 0), 0);
    let run = (acc.balance || 0) - total;
    const series = [run];
    for (const r of rows) series.push((run += r.net || 0));
    return series;
  }, [log, acc.balance]);

  const meta = [acc.platform, `#${acc.login}`, acc.company, acc.leverage ? `1:${acc.leverage}` : null, ccy]
    .filter(Boolean)
    .join(" · ");

  return (
    <section
      id={`ip-${acc.login}-panel`}
      className={`ir-account ir-account-${index}${active ? " is-active" : ""}`}
      aria-labelledby={`ip-${acc.login}-t`}
    >
      <header className="ir-acc-head">
        <div className="ir-acc-id">
          <span className="ir-acc-badge" aria-hidden="true">
            {(acc.label || "??").slice(0, 2).toUpperCase()}
          </span>
          <div>
            <h2 id={`ip-${acc.login}-t`}>{acc.label}</h2>
            <p>{meta}</p>
          </div>
        </div>
        <div className="ir-acc-equity">
          <span className="ir-acc-equity-label">Equity</span>
          <b>{money(acc.equity, ccy, 2)}</b>
          {acc.connected === false ? (
            <span className="ir-delta ir-down">
              <WifiOff size={13} aria-hidden="true" /> offline
            </span>
          ) : (
            <Delta value={acc.returnPct} />
          )}
        </div>
      </header>

      {/* ------------------------------ status ------------------------------ */}
      <div className="ir-block">
        <div className="ir-block-head">
          <h3>Account status</h3>
          <Sparkline
            values={trend}
            baseline={acc.openingBalance}
            positive={!!trend && trend[trend.length - 1] >= (acc.openingBalance || 0)}
          />
        </div>
        <div className="ir-figgrid">
          <Figure label="Balance" value={money(acc.balance, ccy)} sub="realised" />
          <Figure label="Equity" value={money(acc.equity, ccy)} sub="balance + floating" />
          <Figure
            label="Floating P&L"
            value={signed(acc.floating, ccy)}
            cls={`ir-${dir(acc.floating)}`}
            sub={`${positions.length} open`}
          />
          <Figure
            label="Closed P&L"
            value={signed(acc.closedPnl, ccy)}
            cls={`ir-${dir(acc.closedPnl)}`}
            sub="net of costs"
          />
          {/* Closed P&L above is already net of these. Shown so the deduction is
              visible rather than silently baked into the headline figure. Swap
              only when there is any — naming both always overflowed this
              one-line label even on a wide screen. */}
          <Figure
            label="Costs"
            value={signed(acc.costs, ccy)}
            cls={`ir-${dir(acc.costs)}`}
            sub={
              acc.swap
                ? `comm ${signed(acc.commission, ccy)} · swap ${signed(acc.swap, ccy)}`
                : `comm ${signed(acc.commission, ccy)}`
            }
          />
          <Figure label="Deposits" value={money(acc.deposits, ccy)} />
          <Figure label="Withdrawals" value={money(acc.withdrawals, ccy)} cls="ir-down" />
          <Figure label="Net deposits" value={money(acc.netDeposits, ccy)} />
          {/* The base every percentage on this panel is measured against. Shown
              so "+2.39%" is checkable rather than taken on trust. */}
          <Figure label="Opening balance" value={money(acc.openingBalance, ccy)} sub="period start" />
          <Figure
            label="Total P&L"
            value={signed(acc.totalPnl, ccy)}
            cls={`ir-${dir(acc.totalPnl)}`}
            sub={`${pctStr(acc.returnPct, 2)} on ${money(acc.openingBalance, ccy, 0)}`}
          />
          <Figure label="Margin used" value={money(acc.margin, ccy)} />
          <Figure label="Free margin" value={money(acc.freeMargin, ccy)} />
          <Figure label="Margin level" value={plain(acc.marginLevel, 0)} cls={acc.marginLevel ? "ir-up" : ""} />
          <Figure
            label="Open lots"
            value={(acc.lots || 0).toFixed(2)}
            sub={`${stamp(acc.serverTime).time} ${acc.tzLabel || ""}`.trim()}
          />
          {/* LAST card, deliberately. Profit banked by withdrawing it — not the
              cash taken out; that is "Withdrawals" above. */}
          {/* LAST card, and there is deliberately no per-account "Unsettled"
              one after it: the report's panels end here too, and unsettled is
              shown once, on the combined hero. Both figures are still driven by
              the sheet — this is about where they are shown, not what owns
              them. */}
          <Figure
            label="Settled P&L"
            value={signed(acc.settled, ccy)}
            cls={`ir-${dir(acc.settled)}`}
            sub={acc.settled ? "banked by withdrawals" : "nothing banked yet"}
          />
        </div>
      </div>

      {/* --------------------------- live positions -------------------------- */}
      <div className="ir-block">
        <div className="ir-block-head">
          <h3>Live positions</h3>
          <span className="ir-block-note">
            {positions.length} open · {(acc.lots || 0).toFixed(2)} lots
          </span>
        </div>

        {/* The bare message only when NEITHER account has positions — then both
            panels render it and stay level. If the panel beside this one has
            rows, render the table skeleton instead so the two headers, row areas
            and Total rows still line up across the page. */}
        {positions.length === 0 && maxRows.positions === 0 ? (
          <p className="ir-empty">No open positions.</p>
        ) : (
          <>
            <div className="ir-tbl-wrap">
              <table className="ir-tbl ir-tbl-pos">
                <caption className="ir-sr">Open positions for {acc.label}.</caption>
                <ColGroup />
                <thead>
                  <tr>
                    <th scope="col" className="ir-idx">#</th>
                    <th scope="col">Symbol</th>
                    <th scope="col">Side</th>
                    <th scope="col" className="ir-r">Lots</th>
                    <th scope="col" className="ir-r">Open</th>
                    <th scope="col" className="ir-r">Current</th>
                    <th scope="col" className="ir-r">Opened</th>
                    <th scope="col" className="ir-r">Swap</th>
                    <th scope="col" className="ir-r">P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p, i) => (
                    <tr key={p.ticket}>
                      <td className="ir-idx">{i + 1}</td>
                      <td>
                        <span className="ir-sym">{p.symbol}</span>
                        <span className="ir-ticket">#{p.ticket}</span>
                      </td>
                      <td>
                        <span className={`ir-pill ir-pill-${(p.type || "").toLowerCase()}`}>{p.type}</span>
                      </td>
                      <td className="ir-r ir-num">{(p.lots || 0).toFixed(2)}</td>
                      <td className="ir-r ir-num">{(p.openPrice || 0).toFixed(p.digits ?? 2)}</td>
                      <td className="ir-r ir-num ir-bold">{(p.currentPrice || 0).toFixed(p.digits ?? 2)}</td>
                      <td className="ir-r">
                        <DateTime unix={p.openTime} />
                      </td>
                      <td className={`ir-r ir-num ir-xs ir-${dir(p.swap)}`}>{signed(p.swap, ccy)}</td>
                      <td className={`ir-r ir-num ir-bold ir-${dir(p.floating)}`}>{signed(p.floating, ccy)}</td>
                    </tr>
                  ))}
                  {positions.length === 0 && (
                    <tr className="ir-none">
                      <td colSpan={TABLE_COL_COUNT}>No open positions.</td>
                    </tr>
                  )}
                  {/* The message row above already occupies one row's height. */}
                  <FillerRows count={maxRows.positions - Math.max(positions.length, 1)} cols={TABLE_COL_COUNT} />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3">Total</td>
                    <td className="ir-r ir-num">{(acc.lots || 0).toFixed(2)}</td>
                    <td />
                    <td />
                    <td />
                    <td className={`ir-r ir-num ir-xs ir-${dir(sumBy(positions, "swap"))}`}>
                      {signed(sumBy(positions, "swap"), ccy)}
                    </td>
                    <td className={`ir-r ir-num ir-${dir(acc.floating)}`}>{signed(acc.floating, ccy)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* The table above is hidden in the narrow card view, so the message
                has to exist here too or the block reads as blank. */}
            <ul className="ir-rows">
              {positions.length === 0 && <li className="ir-row-none">No open positions.</li>}
              {positions.map((p, i) => (
                <li key={p.ticket}>
                  <span className="ir-row-top">
                    <span className="ir-row-idx">{i + 1}</span>
                    <span className="ir-sym">{p.symbol}</span>
                    <span className={`ir-pill ir-pill-${(p.type || "").toLowerCase()}`}>{p.type}</span>
                    <span className="ir-num ir-xs ir-muted">{(p.lots || 0).toFixed(2)} lots</span>
                    <b className={`ir-num ir-row-pnl ir-${dir(p.floating)}`}>{signed(p.floating, ccy)}</b>
                  </span>
                  <span className="ir-row-mid ir-num">
                    {(p.openPrice || 0).toFixed(p.digits ?? 2)} <i aria-hidden="true">→</i>{" "}
                    <b>{(p.currentPrice || 0).toFixed(p.digits ?? 2)}</b>
                  </span>
                  <span className="ir-row-foot">
                    <em>
                      Opened {stamp(p.openTime).date} {stamp(p.openTime).time}
                    </em>
                    <em className={`ir-${dir(p.swap)}`}>swap {signed(p.swap, ccy)}</em>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* ------------------------------ trade log --------------------------- */}
      <div className="ir-block ir-block-log">
        <div className="ir-block-head">
          <h3>Trade log</h3>
          <span className="ir-block-note">
            last {log.length} closed · P&amp;L net of commission &amp; swap
          </span>
        </div>

        {log.length === 0 && maxRows.log === 0 ? (
          <p className="ir-empty">No closed trades in the reporting window.</p>
        ) : (
          <>
            <div className="ir-tbl-wrap ir-log-scroll">
              <table className="ir-tbl ir-tbl-log">
                <caption className="ir-sr">Closed trades for {acc.label}.</caption>
                <ColGroup />
                <thead>
                  <tr>
                    <th scope="col" className="ir-idx">#</th>
                    <th scope="col">Symbol</th>
                    <th scope="col">Side</th>
                    <th scope="col" className="ir-r">Lots</th>
                    <th scope="col" className="ir-r">Open</th>
                    <th scope="col" className="ir-r">Close</th>
                    <th scope="col" className="ir-r">Opened</th>
                    <th scope="col" className="ir-r">Closed</th>
                    <th scope="col" className="ir-r">P&amp;L / comm.</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((t, i) => (
                    <tr key={t.ticket}>
                      <td className="ir-idx">{i + 1}</td>
                      <td>
                        <span className="ir-sym">{t.symbol}</span>
                        <span className="ir-ticket">#{t.ticket}</span>
                      </td>
                      <td>
                        <span className={`ir-pill ir-pill-${(t.type || "").toLowerCase()}`}>{t.type}</span>
                      </td>
                      <td className="ir-r ir-num">{(t.lots || 0).toFixed(2)}</td>
                      <td className="ir-r ir-num">{(t.openPrice || 0).toFixed(t.digits ?? 2)}</td>
                      <td className="ir-r ir-num">{(t.closePrice || 0).toFixed(t.digits ?? 2)}</td>
                      <td className="ir-r">
                        <DateTime unix={t.openTime} />
                      </td>
                      <td className="ir-r">
                        <DateTime unix={t.closeTime} />
                      </td>
                      {/* Commission stacked under the P&L it was deducted from,
                          rather than taking a column the positions table would
                          have to leave empty. Always rendered, including at zero,
                          so every row is the same height and the two panels'
                          Total rows stay on one line. */}
                      <td className="ir-r">
                        <span className={`ir-num ir-bold ir-${dir(t.net)}`}>{signed(t.net, ccy)}</span>
                        <span className={`ir-cell-sub ir-num ir-${dir(t.commission)}`}>
                          comm {signed(t.commission, ccy)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {log.length === 0 && (
                    <tr className="ir-none">
                      <td colSpan={TABLE_COL_COUNT}>No closed trades in the reporting window.</td>
                    </tr>
                  )}
                  <FillerRows count={maxRows.log - Math.max(log.length, 1)} cols={TABLE_COL_COUNT} />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3">Shown trades</td>
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td className="ir-r">
                      <span className={`ir-num ir-${dir(sumBy(log, "net"))}`}>{signed(sumBy(log, "net"), ccy)}</span>
                      <span className={`ir-cell-sub ir-num ir-${dir(sumBy(log, "commission"))}`}>
                        comm {signed(sumBy(log, "commission"), ccy)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <ul className="ir-rows ir-rows-log">
              {log.length === 0 && <li className="ir-row-none">No closed trades in the reporting window.</li>}
              {log.map((t, i) => (
                <li key={t.ticket}>
                  <span className="ir-row-top">
                    <span className="ir-row-idx">{i + 1}</span>
                    <span className="ir-sym">{t.symbol}</span>
                    <span className={`ir-pill ir-pill-${(t.type || "").toLowerCase()}`}>{t.type}</span>
                    <span className="ir-num ir-xs ir-muted">{(t.lots || 0).toFixed(2)} lots</span>
                    <b className={`ir-num ir-row-pnl ir-${dir(t.net)}`}>{signed(t.net, ccy)}</b>
                  </span>
                  <span className="ir-row-mid ir-num">
                    {(t.openPrice || 0).toFixed(t.digits ?? 2)} <i aria-hidden="true">→</i>{" "}
                    <b>{(t.closePrice || 0).toFixed(t.digits ?? 2)}</b>
                  </span>
                  <span className="ir-row-foot">
                    <em>
                      {stamp(t.closeTime).date} {stamp(t.closeTime).time}
                    </em>
                    <em className={`ir-${dir(t.commission)}`}>comm {signed(t.commission, ccy)}</em>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

/* ============================================================================
   PAGE
   ========================================================================== */

export default function InvestorPortfolio() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkedAt, setCheckedAt] = useState(0);
  const [theme, setTheme] = useState("dark");
  /* Which broker the narrow layout is showing. Above 900px both panels are on
     screen and CSS ignores this, so it costs nothing to keep mounted. Without
     it the shared stylesheet hides EVERY panel on a phone - .ir-split >
     .ir-account is display:none unless it carries .is-active. */
  const [activeAcc, setActiveAcc] = useState(0);
  const [, forceTick] = useState(0);
  const timer = useRef(null);
  const rootRef = useRef(null);
  const topbarRef = useRef(null);

  const load = useCallback(async (force) => {
    setBusy(true);
    try {
      const res = await fetch(force ? `${ENDPOINT}/refresh` : ENDPOINT, { cache: "no-store" });
      const body = await res.json();
      setData(body);
      setError(null);
      setCheckedAt(Date.now());
    } catch (e) {
      // Keep whatever is on screen. A page that empties itself on one failed
      // poll is worse than one showing the previous figures.
      setError(e.message || "could not reach the server");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    timer.current = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(timer.current);
  }, [load]);

  // Re-render once a second so the "checked Ns ago" label stays honest.
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  /* Publish the sticky top bar's height as --ir-topbar-h, exactly as the report
     does. The shared stylesheet offsets content by that variable, so leaving it
     unset puts the first card under the bar at narrow widths. */
  useEffect(() => {
    const bar = topbarRef.current;
    const root = rootRef.current;
    if (!bar || !root) return undefined;
    const apply = () => root.style.setProperty("--ir-topbar-h", `${bar.offsetHeight}px`);
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [data]);

  // Publicly reachable and shows account figures, so keep it out of search
  // engines. Set on mount rather than in index.html so the rest of the site
  // stays indexable.
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow, noarchive";
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);

  const accounts = useMemo(() => (data && data.accounts) || [], [data]);

  /* Both panels' tables padded to the same number of rows, so their Total rows
     land on the same line. Memoised on `accounts` so a poll that changes no row
     count does not rebuild it. */
  const maxRows = useMemo(
    () => ({
      positions: Math.max(0, ...accounts.map((a) => (a.positions || []).length)),
      log: Math.max(0, ...accounts.map((a) => (a.log || []).length)),
    }),
    [accounts]
  );

  const c = (data && data.combined) || null;
  const ccy = (c && c.currency) || "USD";
  /* The eyebrow reads "N/M live". An account the poller cannot reach still shows
     its sheet figures, so this counts the feed, not the figures. */
  const connectedCount = accounts.filter((a) => a.connected !== false).length;
  const title = (data && data.title) || "Investment Portfolio";

  const chrome = (body) => (
    <div className={`ir-root ir-theme-${theme}`} data-ir-theme={theme} ref={rootRef}>
      <div className="ir-glow" aria-hidden="true" />

      <header className="ir-topbar" ref={topbarRef}>
        <div className="ir-topbar-inner">
          <div className="ir-brand">
            <img src="/firstbench-logo.png" alt="" className="ir-brand-logo" />
            <div>
              <div className="ir-brand-name">{title}</div>
              <div className="ir-brand-sub">
                {accounts.length ? accounts.map((a) => a.label).join(" + ") : "connecting…"}
                {data && data.pairStartDate
                  ? ` · since ${stamp(Date.parse(`${data.pairStartDate}T00:00:00Z`) / 1000).date}`
                  : ""}
              </div>
            </div>
          </div>

          <div className="ir-topbar-right">
            <span className={`ir-live${error ? " is-off" : ""}`} title={error ? "Reconnecting" : "Streaming"}>
              <span className="ir-live-dot" aria-hidden="true" />
              {error ? "reconnecting…" : `checked ${ago(checkedAt)}`}
            </span>

            <button type="button" className="ir-btn" onClick={() => load(true)} disabled={busy} title="Refresh now">
              <RefreshCw size={15} aria-hidden="true" />
            </button>

            <button
              type="button"
              className="ir-btn ir-btn-icon"
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {theme === "dark" ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      <main className="ir-main">{body}</main>
    </div>
  );

  if (!data) {
    return chrome(<p className="ir-empty">{error ? `Could not load: ${error}` : "Loading…"}</p>);
  }

  /* Not configured, or the figures could not be built. Said plainly — this page
     never falls back to figures of its own, which is the whole reason it exists.
     The reason given away nothing about where the figures come from. */
  if (!data.ok) {
    return chrome(<div className="ir-banner is-warn">{data.reason}</div>);
  }

  return chrome(
    <>
      {/* The hero. Same section, same order, same cards as the report: the
          headline is EQUITY, the percentage and the absolute total P&L sit under
          it, then a chip per account, then the figure grid.

          No "past accounts" chips: the report grows them from account usages
          that have been retired, and the accounts here are whatever the sheet's
          Accounts tab lists — there is no retired account to name. */}
      <section className="ir-combined" aria-labelledby="ip-comb-t">
        <div className="ir-comb-hero">
          <span className="ir-eyebrow">
            <Layers size={13} aria-hidden="true" /> Combined · {connectedCount}/{c.accountCount} live
          </span>
          <h1 id="ip-comb-t" className="ir-hero-figure">
            {money(c.equity, ccy, 2)}
          </h1>
          <div className="ir-hero-deltas">
            <Delta value={c.returnPct} />
            <span className="ir-hero-abs">{signed(c.totalPnl, ccy, 2)} total P&amp;L</span>
          </div>
          <div className="ir-comb-chips">
            {accounts.map((a, i) => (
              <span key={a.login || a.key} className={`ir-chip ir-chip-${i}`}>
                <b>{a.label}</b>
                {money(a.equity, a.currency || ccy, 0)}
                <em className={`ir-${dir(a.periods && a.periods.today.pnl)}`}>
                  {signed((a.periods && a.periods.today.pnl) || 0, a.currency || ccy, 0)}
                </em>
              </span>
            ))}
          </div>
          <div className="ir-trust">
            <BadgeCheck size={14} aria-hidden="true" />
            Live read-only feed · figures in {ccy}
          </div>
        </div>

        <div className="ir-comb-figs">
          <Figure label="Total deposited" value={money(c.deposits, ccy)} />
          <Figure label="Total withdrawn" value={money(c.withdrawals, ccy)} cls="ir-down" />
          <Figure label="Net deposits" value={money(c.netDeposits, ccy)} />
          <Figure label="Balance" value={money(c.balance, ccy)} />
          {/* Base for the headline percentage above. */}
          <Figure label="Opening balance" value={money(c.openingBalance, ccy)} sub="period start" />
          <Figure label="Closed P&L" value={signed(c.closedPnl, ccy)} cls={`ir-${dir(c.closedPnl)}`} />
          <Figure label="Floating P&L" value={signed(c.floating, ccy)} cls={`ir-${dir(c.floating)}`} />
          <Figure
            label="Open positions"
            value={String(c.openPositions || 0)}
            sub={`${(c.openLots || 0).toFixed(2)} lots`}
          />
          <Figure label="Margin used" value={money(c.margin, ccy)} sub={`free ${compact(c.freeMargin, ccy)}`} />
          {/* Immediately BEFORE Settled so the pair reads together and Settled
              stays the final card, exactly as on the report.

              The sub-line names how much of this was earned BEFORE the current
              pair, because without it the figure has nowhere to live: unsettled
              is measured over the whole record while the balance beside it
              carries only this pair, so "$40,000 not yet withdrawn" sat above a
              balance explaining $1,650 of it. The rest was earned in accounts
              that have since been closed. Only shown when there is any, so a
              first pair reads plainly. */}
          <Figure
            label="Unsettled P&L"
            value={signed(c.unsettled, ccy)}
            cls={`ir-${dir(c.unsettled)}`}
            sub={
              (c.unsettled || 0) - (c.closedPnl || 0) > 0.005
                ? `incl. ${signed((c.unsettled || 0) - (c.closedPnl || 0), ccy, 0)} before this pair`
                : "not yet withdrawn"
            }
          />
          {/* LAST card, deliberately. No count of withdrawals beside it: the
              report counts settlement events it replayed, and here settled is a
              figure rather than a list of events. */}
          <Figure
            label="Settled P&L"
            value={signed(c.settled, ccy)}
            cls={`ir-${dir(c.settled)}`}
            sub={c.settled ? "banked by withdrawals" : "not yet settled"}
          />
        </div>
      </section>

      <section className="ir-card">
        <div className="ir-block-head">
          <h3>
            <Wallet size={15} aria-hidden="true" /> Combined statistics
          </h3>
          {/* These buckets span EVERY account that traded since the overall
              start date - a wider scope than the money figures above them, which
              cover the current pair only. Saying so is what stops "302 trades"
              looking like a fault beside a twenty-row log. */}
          <span className="ir-block-note">
            {c.statsScope
              ? `all accounts since ${stamp(c.statsScope.since).date}` +
                ` · ${c.statsScope.usageCount} account periods`
              : "both accounts together"}
          </span>
        </div>
        <PeriodStats periods={c.periods} ccy={ccy} base={c.openingBalance} size="md" />
      </section>

      {/* Narrow screens show ONE broker at a time rather than stacking both,
          which made the page enormous to scroll. CSS hides this above the switch
          breakpoint, where both panels sit side by side and `active` is
          ignored. */}
      {accounts.length > 1 && (
        <div className="ir-acc-switch" role="tablist" aria-label="Choose broker">
          {accounts.map((a, i) => (
            <button
              key={a.login || a.key}
              type="button"
              role="tab"
              aria-selected={i === activeAcc}
              aria-controls={`ip-${a.login}-panel`}
              className={`ir-acc-tab ir-acc-tab-${i}${i === activeAcc ? " is-on" : ""}`}
              onClick={() => setActiveAcc(i)}
            >
              <span className="ir-acc-tab-name">{a.label}</span>
              <span className={`ir-acc-tab-val ir-${dir(a.totalPnl)}`}>{signed(a.totalPnl, ccy, 0)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="ir-split">
        {accounts.map((a, i) => (
          <AccountPanel
            key={a.login || a.key}
            acc={a}
            index={i}
            maxRows={maxRows}
            /* Clamped: an account dropping out of the payload must not leave
               every panel hidden on a narrow screen. */
            active={i === Math.min(activeAcc, accounts.length - 1)}
          />
        ))}
      </div>

      <footer className="ir-footer">
        <div className="ir-footer-brand">
          <img src="/firstbench-logo.png" alt="" />
          <div>
            <b>{title}</b>
            <span>
              live feed
              {data.pairStartDate
                ? ` · reporting from ${stamp(Date.parse(`${data.pairStartDate}T00:00:00Z`) / 1000).date}`
                : ""}
              {data.tzLabel ? ` · times in ${data.tzLabel}` : ""}
            </span>
          </div>
        </div>
        {/* The report's wording, with one change: it ends "all times are broker
            server time", and every timestamp on this page is in the report zone
            instead — the poller converts at the boundary, so both brokers read
            in one zone. */}
        <p className="ir-disclaimer">
          Trading on margin carries a high level of risk and can result in the loss of all capital. Past
          performance is not indicative of future results. Sizes and results are shown against a fixed
          capital base, so a trade keeps the price move it caught. Balance is realised P&amp;L only; equity
          carries the floating on open positions on top of it. Open positions are marked to the last quoted
          price; all times are shown in {data.tzLabel || "the report time zone"}. This document is for the
          named recipient only and is not an offer to buy or sell any financial instrument.
        </p>
      </footer>
    </>
  );
}
